<template>
  <div
    class="field-box"
    :class="[`field-box--${type.toLowerCase()}`, { selected }]"
    @click="$emit('click')"
  >
    <button
      v-if="closable && selected"
      type="button"
      class="field-box__close"
      title="Eliminar campo"
      @click.stop="$emit('close')"
      @mousedown.stop
      @touchstart.stop="$emit('close')"
    >
      ×
    </button>
    <div class="field-box__body" :style="bodyStyle">
      <span v-if="isCheckbox" class="field-box__checkmark" :style="{ fontSize: `${checkboxSize}px` }">☐</span>
      <template v-else>
        <span class="field-box__label">
          {{ label }}<span v-if="required" class="field-box__req">*</span>
        </span>
        <span class="field-box__sample">{{ sample }}</span>
      </template>
    </div>
  </div>
</template>

<script>

import {
  FIELD_TYPES,
  sampleValueForType,
} from '@/febos/global/pdf/formBuilder/model/fieldModel';

/**
 * Presentación de un campo según su tipo semántico. Rellena el contenedor
 * (el tamaño real lo controla DraggableField). No contiene lógica de drag.
 *
 * La fuente del label escala con la altura del campo (boxHeight) para que un
 * campo alto no muestre texto diminuto. Muestra además un valor de ejemplo
 * atenuado como previsualización.
 */
export default {
  name: 'FieldBox',
  props: {
    type: { type: String, required: true },
    label: { type: String, default: '' },
    required: { type: Boolean, default: false },
    placeholder: { type: String, default: '' },
    boxHeight: { type: Number, default: 0 },
    // Tamaño de fuente configurado (en px pantalla, ya escalado por zoom). Si
    // no viene, se usa el tamaño proporcional a la altura de la caja.
    fontSizePx: { type: Number, default: null },
    // Alineación del texto: left | center | right.
    alignment: { type: String, default: 'left' },
    selected: { type: Boolean, default: false },
    closable: { type: Boolean, default: false },
  },
  computed: {
    isCheckbox() {
      return this.type === FIELD_TYPES.CHECKBOX;
    },
    // Fuente proporcional a la altura del campo, acotada a un rango legible.
    // Es el fallback cuando no hay un fontSize configurado.
    fontSize() {
      const h = this.boxHeight || 18;
      return Math.max(8, Math.min(16, Math.round(h * 0.55)));
    },
    // Tamaño efectivo: el configurado (si viene) o el proporcional.
    effectiveFontSize() {
      return this.fontSizePx != null && this.fontSizePx > 0
        ? this.fontSizePx
        : this.fontSize;
    },
    checkboxSize() {
      const h = this.boxHeight || 14;
      return Math.max(10, Math.min(28, Math.round(h * 0.9)));
    },
    justifyContent() {
      switch (this.alignment) {
        case 'center':
          return 'center';
        case 'right':
          return 'flex-end';
        default:
          return 'flex-start';
      }
    },
    bodyStyle() {
      return {
        fontSize: `${this.effectiveFontSize}px`,
        justifyContent: this.justifyContent,
      };
    },
    sample() {
      return this.placeholder || sampleValueForType(this.type);
    },
  },
};

</script>

<style lang="scss" scoped>

.field-box {
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  border: 1px dashed #7367f0;
  background: rgba(115, 103, 240, 0.08);
  cursor: move;
  position: relative;
  /* sin overflow:hidden aquí para no recortar el botón de borrar que sobresale
     por encima del borde superior; el recorte del texto se hace en __body. */
  display: flex;
  align-items: center;
}

.field-box.selected {
  border-style: solid;
  border-color: #7367f0;
  background: rgba(115, 103, 240, 0.16);
}

.field-box__body {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0 4px;
  width: 100%;
  overflow: hidden;
  pointer-events: none;
}

.field-box__label {
  font-size: inherit;
  font-family: sans-serif;
  color: #4b4b6b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 0 0 auto;
  max-width: 100%;
}

.field-box__sample {
  font-size: inherit;
  font-family: sans-serif;
  color: #a7a7c0;
  font-style: italic;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1 1 auto;
  min-width: 0;
}

.field-box__req {
  color: #ea5455;
  margin-left: 1px;
}

.field-box__checkmark {
  line-height: 1;
  margin: 0 auto;
}

.field-box__close {
  position: absolute;
  /* Centrado por fuera del borde superior: no choca con los handles de
     esquina (nw/ne/sw/se) de vue-moveable. */
  top: -30px;
  left: 50%;
  transform: translateX(-50%);
  width: 24px;
  height: 24px;
  padding: 0;
  line-height: 20px;
  text-align: center;
  background: #ea5455;
  color: #fff;
  border: 2px solid #fff;
  border-radius: 50%;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  z-index: 100;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.45);
  pointer-events: auto;
}

.field-box__close:hover {
  background: #c0392b;
  transform: translateX(-50%) scale(1.1);
}

/* Firma / iniciales: zona destacada, no texto */
.field-box--signature,
.field-box--initials {
  border-color: #28c76f;
  background: rgba(40, 199, 111, 0.1);
}

.field-box--signature.selected,
.field-box--initials.selected {
  border-color: #28c76f;
  background: rgba(40, 199, 111, 0.18);
}

</style>
